import { CoachRow, CenterAvg, COACH_CENTER_AVG } from './tabData';

/**
 * 강사 강점·약점 분석.
 *
 * 지표 아홉 개를 표로 늘어놓으면 원장은 "그래서 이 강사가 잘하는 거야 못하는 거야"를
 * 스스로 계산해야 한다. 아홉 개를 아홉 번 읽고 평균과 비교하는 일은 아무도 안 한다.
 *
 * 그래서 여기서 대신 판정한다.
 *   ① 센터 평균 대비 편차를 지표마다 같은 척도로 환산하고
 *   ② 의미 있는 차이(15% 이상)만 남기고
 *   ③ 잘하는 것 / 못하는 것 순으로 정렬한다.
 *
 * 판정 기준을 코드 한 곳에 두는 이유는 화면마다 "잘한다"의 뜻이 갈리면 안 되기 때문이다.
 */

/** 이 편차부터 의미 있다고 본다. 그 밑은 표본 흔들림으로 본다 */
export const MEANINGFUL_DEVIATION = 0.15;

/* ═══════════════════════════════════════════════════════════
   평가 유보 — 표본이 모자라면 평가하지 않는다
   ═══════════════════════════════════════════════════════════ */

/**
 * 평가를 시작할 수 있는 최소 담당 활성 회원 수.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 5명인가 — 분모가 회원 수인 지표들 때문이다
 * ─────────────────────────────────────────────────────────────
 * `conversion`(재등록 성공률)의 분모는 **그 달에 정상 종료한 회원 수**이고
 * `closing`(신규기회 클로징률)의 분모는 **배정받은 리드 회원 수**다
 * (서버 계약 `CoachRowDto.basis.conversion` / `.closing`).
 * 담당이 1명이면 그 한 명이 재등록했는지 아닌지로 성공률이 0% 아니면 100%가 된다.
 * 100%는 "이 강사가 잘한다"가 아니라 "한 명이 남았다"는 뜻이다.
 *
 * 이 값은 원래 reportComposer 에만 있었다(MIN_MEMBERS_FOR_COMPARE).
 * PDF 는 그 기준으로 이미 평가를 접고 있었는데 **화면에는 같은 가드가 없어서**,
 * 같은 강사가 화면에서는 '핵심 인재'이고 인쇄물에서는 '판단 보류'였다.
 * 기준을 여기 한 곳에 두고 양쪽이 같이 쓴다.
 */
export const MIN_ACTIVE_MEMBERS_FOR_JUDGEMENT = 5;

/**
 * 평가를 시작할 수 있는 최소 수업 수(집계 기간 기준).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 10회인가 — 분모가 수업 수인 지표들 때문이다
 * ─────────────────────────────────────────────────────────────
 * `journal`(수업일지 작성률)과 `noshow`(담당 회원 노쇼율)의 분모는 수업·예약 수다
 * (서버 계약 `CoachRowDto.basis.journal` / `.noshow`).
 *
 * 수업이 M회면 그 비율이 가질 수 있는 값은 M+1 개뿐이고, 눈금 간격은 100/M 퍼센트포인트다.
 *   · M=3  → 0 / 33 / 67 / 100 네 값뿐. 센터 평균 근처(예: 48%)에 **놓일 수 있는 값이 아예 없다.**
 *            그래서 무조건 크게 위 아니면 크게 아래로 잡히고, 늘 '강점' 아니면 '약점'이 된다.
 *            이것이 지금 '수업 3회 강사가 핵심 인재'가 된 경로다.
 *   · M=10 → 눈금 간격 10pp. 수업 한 번이 비율을 10pp 넘게 흔들지 못한다.
 *
 * 그래서 눈금 간격이 10pp 이하가 되는 지점을 최소 기준으로 잡는다 → M ≥ 10.
 *
 * ⚠️ 이 값은 "10회면 충분하다"가 아니라 "10회 아래는 확실히 못 쓴다"는 뜻이다.
 *    올릴 근거가 생기면 올려도 되지만, 내리려면 위 산수를 다시 해야 한다.
 */
export const MIN_LESSONS_FOR_JUDGEMENT = 10;

/* ═══════════════════════════════════════════════════════════
   판정 분모 — 월 스코프 (백엔드 f78c887c, 2026-08-31)
   ═══════════════════════════════════════════════════════════ */

/**
 * 강사를 평가할 때 쓰는 **회원 수 분모**.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 `activeMembers` 를 직접 쓰면 안 되나
 * ─────────────────────────────────────────────────────────────
 * `activeMembers` 는 **오늘** 활성인 회원 수입니다. 선택한 달을 안 따라갑니다.
 * 그런데 분자(`lessons`)는 **그 달**의 수업 수라서, 지난달 리포트를 열면
 * 「7월 수업 ÷ 8월 담당」이 나왔습니다. 담당이 크게 바뀐 강사일수록 어긋나고,
 * 시간이 갈수록 더 벌어집니다.
 *
 * 서버가 `monthlyMembers`(그 달 실제 담당)를 주기 시작했으므로 그 값을 씁니다.
 * 실측(2026-08-31 라이브): 19명 중 12~13명의 분모가 실제로 달랐고, 7월 조은오는
 * 9.7회 → 6.9회로 바뀝니다.
 *
 * ⚠️ 키가 없으면 **구버전 서버**라 예전 값으로 떨어집니다. 프론트가 서버보다
 *    먼저 배포돼도 화면이 안 깨집니다.
 *
 * ⚠️ 이 함수를 **세 곳이 함께** 씁니다 — 수업 밀도(분모), 표본 판정(checkSample),
 *    리포트 성숙도(reportComposer.resolveMaturity). 하나만 옮기면
 *    「월간 담당 1명인데 표본 10명 통과」 같은 모순이 생깁니다.
 */
export const judgementMembers = (c: Pick<CoachRow, 'activeMembers' | 'monthlyMembers'>): number =>
    c.monthlyMembers ?? c.activeMembers;

/** 이 강사의 분모가 월 스코프인가 (서버가 `monthlyMembers` 를 보냈는가) */
export const usesMonthlyScope = (
    c: Pick<CoachRow, 'activeMembers' | 'monthlyMembers'>
): boolean => c.monthlyMembers != null;

/* ═══════════════════════════════════════════════════════════
   밀도 분모 — 「수업 회원」
   ═══════════════════════════════════════════════════════════ */

/**
 * 이 강사의 「회원당 수업 수」 분모가 **수업 회원**인가.
 *
 * 🔴 판정 분모(`judgementMembers`)와 **다른 축**입니다.
 *    · 판정·표본·성숙도 → `monthlyMembers ?? activeMembers` (그 달 담당 회원)
 *    · 밀도 분모        → `lessonMembers` (그 달 실제 수업한 회원)
 *    수강권만 있고 한 번도 안 온 잠수회원이 밀도 분모에 들어가면 값이
 *    구조적으로 낮게 나옵니다. 그 회원들은 회원 현황의 이탈 위험에서 따로 봅니다.
 */
export const usesLessonMemberDensity = (
    c: Pick<CoachRow, 'lessonMembers'>
): boolean => typeof c.lessonMembers === 'number';

/**
 * 밀도 분모를 **못 쟀는가** — 서버가 명시적으로 `null` 을 보냈다.
 *
 * 🔴 **`null` 과 `undefined` 는 다른 사실이다.**
 *      `undefined`  이 세대 서버는 「수업 회원」을 아예 안 보낸다 → 옛 분모로 폴백
 *      `null`       보내는 서버인데 **이 강사를 못 쟀다** → 밀도 자체가 없다
 *
 *    `?? judgementMembers` 로 뭉개면 「수업 100회 ÷ 월간 담당 20명 = 5회」가
 *    **정상 측정값**으로 등급·사분면·인쇄물에 들어간다. 그 5회는 아무도 재지 않은
 *    숫자다. 못 잰 것은 못 쟀다고 둔다 — 이 저장소가 이미 쓰는 원칙이다.
 */
export const lessonDensityFailed = (
    c: Pick<CoachRow, 'lessonMembers'>
): boolean => c.lessonMembers === null;

/**
 * 화면에 그대로 쓰는 분모 이름 — 두 기준이 같은 말을 쓰면 안 된다.
 *
 * @param scope 기본값 `'judgement'` — 표본·성숙도·등급이 쓰는 **담당 회원** 분모.
 *              `'density'` 는 「회원당 수업 수」 분모라 서버가 `lessonMembers` 를
 *              보내면 「수업 회원」이 된다.
 *
 * 🔴 기본값을 바꾸지 마세요. `checkSample` 은 이 이름 옆에
 *    `judgementMembers(c)` 를 찍습니다 — 이름만 「수업 회원」으로 바뀌면
 *    「수업 회원 4명」이라 적고 담당 회원 수를 인쇄하게 됩니다.
 */
export const membersLabelOf = (
    c: Pick<CoachRow, 'activeMembers' | 'monthlyMembers' | 'lessonMembers'>,
    scope: 'judgement' | 'density' = 'judgement'
): string => {
    if (scope === 'density' && usesLessonMemberDensity(c)) return '수업 회원';
    return usesMonthlyScope(c) ? '월간 담당' : '활성 회원';
};

/**
 * 밀도 분모 — 서버가 「수업 회원」을 주면 그것, 키가 없으면 기존 판정 분모.
 * 🔴 `null`(집계 실패)이면 **분모가 없다.** 옛 분모로 되살리지 않는다.
 */
export const densityMembers = (
    c: Pick<CoachRow, 'activeMembers' | 'monthlyMembers' | 'lessonMembers'>
): number | null => (lessonDensityFailed(c) ? null : (c.lessonMembers ?? judgementMembers(c)));

/**
 * 회원당 수업 수. 분모가 0이면 나눌 수 없으므로 `null`.
 *
 * 분모는 **그 달에 실제 수업한 회원 수**(`lessonMembers`)다 — 분자를 만든
 * 바로 그 스케줄 집합에서 센 값이라 분자·분모가 어긋나지 않는다.
 * 서버가 안 주면 기존 판정 분모로 떨어진다.
 *
 * 실서버에 **수업은 있는데 담당이 0인 강사**가 실제로 있습니다
 * (2026-08-31 최태현 · 수업 1회 · 월간 담당 0명). 0으로 나눠 Infinity 를
 * 만들지 않고 「못 잰다」로 둡니다 — `deviationOf` 가 null 을 걸러냅니다.
 */
export const lessonDensityOf = (c: CoachRow): number | null => {
    const denom = densityMembers(c);
    return denom != null && denom > 0 ? round1(c.lessons / denom) : null;
};

/**
 * 밀도 비교에 쓸 **센터 평균** — 🔴 분모가 같은 것끼리만 비교합니다.
 *
 * 강사 값은 수업 회원 분모인데 평균은 담당 회원 분모면, 그 비교로 나온 편차는
 * 아무 뜻이 없습니다. 그래서 **쌍이 안 맞으면 `null`** 을 돌려주고 그 지표는
 * 「미측정」이 됩니다 — 틀린 비교보다 안 하는 편이 낫습니다.
 *
 * 세대는 셋이고, 강사 값이 어느 분모로 나왔는지에 따라 평균도 같은 것을 고른다:
 *   `lessonMembers` 있음   → `lessonsPerLessonMember`
 *   `monthlyMembers` 있음  → `lessonsPerMonthlyMember`
 *   둘 다 없음(구서버)     → `lessonsPerActiveMember`
 */
export const lessonDensityAvgOf = (
    avg: CenterAvg | undefined,
    c?: Pick<CoachRow, 'activeMembers' | 'monthlyMembers' | 'lessonMembers'>
): number | null | undefined => {
    if (!avg) return undefined;
    /*
     * 🔴 분모를 못 쟀으면 **짝지을 평균도 없다.** 옛 평균을 끌어다 대면
     *    「못 잰 값 vs 다른 분모의 평균」이라는 비교가 성립한 척을 한다.
     */
    if (c && lessonDensityFailed(c)) return null;
    if (c && usesLessonMemberDensity(c)) return avg.lessonsPerLessonMember ?? null;
    if (c && usesMonthlyScope(c)) return avg.lessonsPerMonthlyMember ?? null;
    return avg.lessonsPerActiveMember;
};

export type SampleCheck = {
    /** 평가를 매겨도 되는가 */
    enough: boolean;
    /** 왜 모자란가 — 화면 배지와 인쇄물에 그대로 나간다 */
    reason: string;
};

/**
 * 표본이 평가를 견딜 만한가.
 *
 * ⚠️ 모자란 강사를 **0점으로 취급하지 않는다.** 낮은 등급도 평가다.
 * 표본 1명으로 매긴 '집중 관리'는 표본 1명으로 매긴 '핵심 인재'와 똑같이 틀렸다.
 * 이 저장소가 이미 쓰는 원칙(NO_CENTER_AVG → NA, 「금액 산정 불가」)과 같다 —
 * 모르는 것은 모른다고 한다.
 */
export const checkSample = (c: CoachRow): SampleCheck => {
    /*
     * 🔴 분모는 `judgementMembers` 하나만 씁니다 — 밀도·성숙도와 **같은 값**입니다.
     *    여기만 `activeMembers` 로 두면 「월간 담당 1명인데 표본 통과」 같은
     *    모순이 생깁니다(백엔드 월 스코프 전환 시 지적된 자리).
     */
    const thinMembers = judgementMembers(c) < MIN_ACTIVE_MEMBERS_FOR_JUDGEMENT;
    const thinLessons = c.lessons < MIN_LESSONS_FOR_JUDGEMENT;
    if (!thinMembers && !thinLessons) return { enough: true, reason: '' };

    /*
     * 무엇이 모자란지 정확히 적는다.
     * "표본이 적습니다"로 뭉개면 원장이 있지도 않은 문제를 붙든다 —
     * 회원은 8명인데 수업이 4회인 강사에게 "회원을 더 배정하세요"가 되면 안 된다.
     */
    const parts: string[] = [];
    /* 분모가 바뀌면 이름도 바뀐다 — 「활성 회원 4명」과 「월간 담당 1명」은 다른 사실이다 */
    if (thinMembers) parts.push(`${membersLabelOf(c)} ${judgementMembers(c)}명`);
    if (thinLessons) parts.push(`수업 ${c.lessons}회`);
    return { enough: false, reason: parts.join(' · ') };
};

/**
 * 수업량 기준선 — **평가 대상 강사들로만** 만든다.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 함수로 빼는가
 * ─────────────────────────────────────────────────────────────
 * 이 평균은 두 곳에서 쓰인다 — 등급을 매기는 analyzeAll 과, 강사 현황 화면이
 * '지표 펼치기'에서 쓰는 compareMetric. 예전에는 두 곳이 **각자 나눴고**,
 * 한쪽만 유보 강사를 빼면 같은 화면에서 등급의 근거와 눈에 보이는 비교가
 * 다른 기준선을 쓰게 된다. 자는 하나만 둔다.
 *
 * ⚠️ 적격 강사가 하나도 없으면 **0 을 준다 = 기준선 없음**이다.
 * 전원으로 되돌려 평균을 만들면 "유보 강사를 뺀다"는 규칙과 정면으로 어긋나고,
 * 표본이 없는 센터가 자기들끼리 만든 평균으로 서로를 평가하게 된다.
 * 0 이면 deviationOf 가 비교를 포기하고(`!avg`) 그 지표는 '미측정'이 된다 —
 * 이 저장소가 이미 쓰는 방식이다.
 */
export const eligibleLessonsAvg = (rows: CoachRow[]): number => {
    const eligible = rows.filter((c) => checkSample(c).enough);
    if (eligible.length === 0) return 0;
    return round1(eligible.reduce((s, c) => s + c.lessons, 0) / eligible.length);
};

/** 화면 배지에 그대로 쓰는 기준 안내 */
export const SAMPLE_RULE_TEXT =
    `활성 회원 ${MIN_ACTIVE_MEMBERS_FOR_JUDGEMENT}명 이상 · ` +
    `수업 ${MIN_LESSONS_FOR_JUDGEMENT}회 이상부터 평가합니다`;


/**
 * 화면에 그대로 찍히는 수치는 소수 첫째 자리까지만.
 *
 * 평균은 나눗셈에서 나오므로 `43.55555555555556` 같은 값이 그대로 배지에 박힙니다
 * ("수업 진행량 66회 (평균 43.55555555555556회)"). 원장님이 면담 자리에서 보는 숫자라
 * 자릿수가 길면 그 자체로 못 믿을 숫자가 됩니다.
 *
 * ⚠️ **판정에는 쓰지 않습니다.** 편차·등급은 반올림 전 원값으로 계산합니다 —
 *    표시를 다듬느라 경계에 걸친 강사의 등급이 바뀌면 안 됩니다.
 */
export const round1 = (n: number): number => Math.round(n * 10) / 10;

export type MetricKey =
    | 'conversion'
    | 'closing'
    | 'journal'
    | 'journalRead'
    | 'sameDayCancel'
    | 'noshow'
    | 'response'
    | 'lessons'
    | 'lessonDensity'
    | 'injury';

/**
 * 지표 신뢰도 — 4~6차 검증 결과.
 *   VERIFIED  실제 이탈 데이터로 예측력이 확인됨. 판정에 그대로 쓴다.
 *   WEAK      예측력은 있으나 크지 않음. 판정에 절반만 반영한다.
 *   REFERENCE 예측력이 확인되지 않았거나 회원 사정이 섞여 있음.
 *             화면에는 보여주되 "이 강사가 문제다"의 근거로는 쓰지 않는다.
 */
export type MetricConfidence = 'VERIFIED' | 'WEAK' | 'REFERENCE';

export const CONFIDENCE_WEIGHT: Record<MetricConfidence, number> = {
    VERIFIED: 1,
    WEAK: 0.5,
    REFERENCE: 0,
};

export type MetricDef = {
    key: MetricKey;
    label: string;
    confidence: MetricConfidence;
    /** 값이 클수록 좋은 지표인가 */
    higherIsBetter: boolean;
    unit: string;
    /** 왜 이 지표를 보는가 — 원장에게 설명할 한 줄 */
    why: string;
    /** 이 지표가 약할 때 뭘 하면 되는가 */
    action: string;
    /** 강할 때 어떻게 활용하는가 */
    leverage: string;
    get: (c: CoachRow) => number | null;
    /**
     * 센터 평균 — 비교 기준선.
     * 아직 아무도 값이 없는 지표는 null 이고, 그 지표는 비교 자체를 하지 않는다.
     */
    avg: number | null;
};


/**
 * ── 아직 라포가 안 재는 지표 ────────────────────────────────
 *
 * 정의는 살려 두되 METRICS 에는 넣지 않습니다.
 * 서버(opsCoaches.service)가 늘 null 을 보내기 때문입니다 — 집계 경로가 없습니다.
 * 목 데이터에만 값이 있어서 화면에서는 멀쩡해 보였고,
 * 실제 센터에서는 '지표 평가' 열 줄 중 넉 줄이 '—' 로 뜨는 상태였습니다.
 *
 * **서버가 집계를 붙이면 이 배열에서 METRICS 로 옮기면 됩니다.**
 * 옮기기 전에 dataIntegrity 의 "모든 지표가 최소 한 명 이상에서 측정 가능해야 한다"가
 * 통과하는지 확인하세요. 그 테스트가 이 상태를 잡아낸 장치입니다.
 */
export const PLANNED_METRICS: MetricDef[] = [
    {
        key: 'response',
        confidence: 'WEAK',
        label: '예약 응답 속도',
        higherIsBetter: false,
        unit: '시간',
        why: '회원이 수업을 요청하고 답을 받기까지 걸리는 시간. 늦으면 회원이 다른 시간을 찾습니다',
        action: '미응답 요청 알림을 켜고, 반나절 안에 답하도록 기준을 정하세요',
        leverage: '빠른 응답이 회원 만족으로 이어지는 사례로 소개하세요',
        get: (c) => c.responseHours,
        avg: COACH_CENTER_AVG.responseHours,
    },
    {
        key: 'sameDayCancel',
        confidence: 'REFERENCE',
        label: '당일 취소',
        higherIsBetter: false,
        unit: '건',
        why: '수업 당일 취소된 건수. 라포는 회원이 직접 당일 취소를 못 해서 강사가 대신 처리합니다. 회원 사정이 섞여 있어 강사 평가 근거로 쓰지 않습니다',
        action: '취소 사유를 라포에 남기게 해서 회원 사정인지 강사 사정인지 구분하세요',
        leverage: '일정 관리가 안정적이라는 뜻입니다',
        get: (c) => c.sameDayCancel,
        avg: COACH_CENTER_AVG.sameDayCancel,
    },
    {
        key: 'journalRead',
        confidence: 'REFERENCE',
        label: '회원 일지 열람률',
        higherIsBetter: true,
        unit: '%',
        why: '써도 회원이 안 읽으면 관계로 이어지지 않습니다. 작성률과 함께 봐야 합니다',
        action: '수업 끝나고 "일지 올렸어요" 한마디를 덧붙이게 하세요',
        leverage: '회원이 실제로 챙김받는다고 느끼고 있다는 신호입니다',
        get: (c) => c.journalReadPct,
        avg: COACH_CENTER_AVG.journalReadPct,
    },
    {
        key: 'injury',
        confidence: 'REFERENCE',
        label: '담당 회원 부상 기록',
        higherIsBetter: false,
        unit: '%',
        why: '컨디션 기록 중 통증이 포함된 비율. 프로그램 강도를 가늠하는 참고 지표입니다',
        action: '운동 강도와 회원 컨디션 체크 주기를 함께 점검하세요',
        leverage: '안전하게 프로그램을 짜고 있다는 뜻입니다',
        get: (c) => c.injuryPct,
        avg: COACH_CENTER_AVG.injuryPct,
    },
];

export const METRICS: MetricDef[] = [
    {
        key: 'conversion',
        confidence: 'VERIFIED',
        /*
         * 🔴 값은 **그 달에 정상 종료한 회원 전부**가 분모다(all-pass). 수강권 순번을
         *    가리지 않는다. 「1차 → 2차」라고 적으면 첫 수강권만 센 값으로 읽혀서,
         *    같은 숫자에 두 가지 뜻이 붙는다 (2026-09-14 대표 결정).
         *    이름 한 벌은 `scenes/Feed/coachPerformanceModel.RE_REGISTRATION_SUCCESS_NAMES`
         *    와 같은 말이어야 한다 — 화면마다 다른 말을 쓰지 않는다.
         */
        label: '재등록 성공률',
        higherIsBetter: true,
        unit: '%',
        why: '그 달에 정상 종료한 회원 중 이후 재등록한 회원의 비율. 센터 매출 성장을 가장 크게 좌우합니다',
        action: '수강권 잔여 3회 시점에 상담하도록 지시하세요',
        leverage: '이 강사의 상담 방식을 다른 강사와 공유하게 하세요',
        get: (c) => c.conversionPct,
        avg: COACH_CENTER_AVG.conversionPct,
    },
    {
        key: 'closing',
        confidence: 'VERIFIED',
        label: '신규기회 클로징률',
        higherIsBetter: true,
        unit: '%',
        why: '체험·OT로 만난 신규 회원을 실제 결제까지 데려온 비율. 손으로 찍은 성공 여부가 아니라 실제 수강권 결제 기준입니다',
        action: '체험 마지막 회차에 다음 단계를 반드시 제안하도록 정하세요. 클로징은 타이밍이 전부입니다',
        leverage: '이 강사의 체험 진행 방식을 다른 강사가 참관하게 하세요',
        get: (c) => c.closingRate,
        avg: COACH_CENTER_AVG.closingRate,
    },
    {
        key: 'journal',
        confidence: 'REFERENCE',
        label: '수업일지 작성률',
        higherIsBetter: true,
        unit: '%',
        why: '성실도를 보여주는 기록입니다. 다만 이탈을 막는다는 근거는 아직 없습니다',
        action: '수업 직후 3분 안에 쓰도록 습관을 잡아주세요',
        leverage: '성실함을 칭찬할 때 구체적으로 짚을 수 있는 지점입니다',
        get: (c) => c.journalPct,
        avg: COACH_CENTER_AVG.journalPct,
    },
    {
        key: 'noshow',
        confidence: 'WEAK',
        label: '담당 회원 노쇼율',
        higherIsBetter: false,
        unit: '%',
        why: '담당 회원이 말없이 안 오는 비율. 개인 성향보다 시간대 배정 문제인 경우가 많습니다',
        action: '노쇼가 잦은 회원의 수업 요일·시간대를 바꿔보세요',
        leverage: '회원 일정 관리가 잘 되고 있다는 뜻입니다',
        get: (c) => c.noshowPct,
        avg: COACH_CENTER_AVG.noshowPct,
    },
    {
        key: 'lessonDensity',
        confidence: 'WEAK',
        /* 분모 이름을 라벨에 박지 않는다 — 서버 세대에 따라 「월간 담당」/「활성 회원」이 갈린다 */
        label: '회원당 수업 수',
        higherIsBetter: true,
        unit: '회',
        /*
         * ✅ **분자와 분모의 기준시각이 맞았다** (백엔드 f78c887c, 2026-08-31).
         *
         * 예전에는 분자 `lessons` 가 **선택한 달**인데 분모 `activeMembers` 는
         * **오늘**이라, 지난달 리포트가 「7월 수업 ÷ 8월 담당」이었다.
         * 서버가 `monthlyMembers`(그 달 실제 담당)를 주기 시작해 둘 다 그 달
         * 기준이 됐다. 실측 7월 조은오 9.7회 → 6.9회.
         *
         * 이 지표는 `confidence: 'WEAK'` 라 **등급에 반영된다.** 그래서 분모가
         * 틀리면 지난달 등급이 흔들렸다 — 이제 그 원인이 사라졌다.
         * (실측: 8월·7월 19명 전원 등급 변동 없음. 값만 정확해졌다)
         *
         * 🆕 **분모가 「수업 회원」으로 한 번 더 바뀌었다** (2026-09-10).
         *    수강권만 있고 한 번도 안 온 잠수회원이 분모에 있어 값이 구조적으로
         *    낮았다. 이제 분자를 만든 바로 그 스케줄 집합의 회원 수로 나눈다.
         *
         * ⚠️ 분모와 평균은 `densityMembers` / `lessonDensityAvgOf` 한 쌍으로만
         *    고른다. 여기에 식을 다시 적지 말 것.
         */
        why:
            '수업량을 그 달에 수업한 회원 수로 나눈 값입니다. ' +
            '수강권은 있지만 한 번도 안 온 회원은 빼고 셉니다 — 그 회원들은 회원별 상태의 이탈 위험에서 따로 봅니다. ' +
            '※ 서버가 수업 회원 수를 주지 않는 경우에만 그 달 담당 회원 수로 나눕니다',
        action: '담당 회원 중 최근 수업이 뜸한 사람이 누구인지 회원 현황에서 확인하세요',
        leverage: '회원을 촘촘하게 끌고 오고 있습니다. 배정을 늘려도 될 여력이 있는지 함께 보세요',
        // 담당이 0명이면 나눌 수 없다. deviationOf 가 null 을 걸러낸다.
        get: (c) => lessonDensityOf(c),
        avg: COACH_CENTER_AVG.lessonsPerActiveMember,
    },
    {
        key: 'lessons',
        confidence: 'REFERENCE',
        label: '수업 진행량',
        higherIsBetter: true,
        unit: '회',
        why:
            '센터 매출에 직접 기여하는 총량입니다. 담당 회원 수에 따라 달라져서 이 값만으로는 잘잘못을 ' +
            '가르지 않습니다. 판단은 바로 위 「활성 회원당 수업 수」로 합니다',
        action: '담당 회원 배정을 늘리거나 스케줄 여유를 확인하세요',
        leverage: '기여가 큰 만큼 보상과 일정 여유를 함께 챙기세요',
        get: (c) => c.lessons,
        // 수업량은 센터 평균이 상수로 없어 목록에서 계산해 넣는다
        avg: 0,
    },
];

export type Deviation = {
    metric: MetricDef;
    /** 화면 표시용 — 소수 첫째 자리까지 반올림된 값 (판정은 원값으로 했다) */
    value: number;
    /** 화면 표시용 — 소수 첫째 자리까지 반올림된 센터 평균 */
    avg: number;
    /** 평균 대비 몇 % 좋은가(양수) 나쁜가(음수) */
    score: number;
    /** 신뢰도를 반영한 판정용 점수. REFERENCE 지표는 0이 된다 */
    weightedScore: number;
    /** 화면용 — 평균 대비 증감(부호 있는 %) */
    diffPct: number;
    kind: 'STRENGTH' | 'WEAKNESS' | 'NEUTRAL';
    /** 등급·1순위 문제를 정할 때 근거로 쓸 수 있는가 */
    judgeable: boolean;
    /**
     * 값 옆에 작게 붙는 분모 문구 — 전환율만 「N명 중 M명」.
     * 편차 막대는 값만 크게 찍어서, 표와 달리 「0%」가 몇 명 중 몇 명인지 안 보였다.
     * 「% 단독 표시 금지」는 막대 줄에도 적용한다.
     */
    detail?: string;
};

/**
 * 편차 계산.
 * 지표마다 단위가 달라서(%, 건, 시간) 그대로 비교할 수 없다.
 * 평균 대비 비율로 환산해 같은 자로 잰다.
 */
export const deviationOf = (
    c: CoachRow,
    metric: MetricDef,
    /**
     * undefined = 기준을 안 넘김 → METRICS 기본값을 쓴다 (목 모드)
     * null      = 기준을 넘겼는데 그 값이 없음 → **비교하지 않는다**
     *
     * 둘을 ?? 로 뭉개면 안 됩니다. null 을 기본값으로 흘리면
     * 이제 막 문을 연 센터가 목 데이터 상수와 비교당합니다.
     */
    avgOverride?: number | null
): Deviation | null => {
    const value = metric.get(c);
    const avg = avgOverride === undefined ? metric.avg : avgOverride;
    // 표본이 없거나(전환율 null) 기준이 0이면 비교 자체가 성립하지 않는다
    if (value == null || !avg) return null;

    // 편차는 **원값**으로 계산한다. 반올림한 값으로 재면 경계에 걸친 강사의 등급이 흔들린다
    const raw = (value - avg) / avg;
    const score = metric.higherIsBetter ? raw : -raw;

    const weight = CONFIDENCE_WEIGHT[metric.confidence];

    /*
     * 전환율(재등록 성공률)만 값 옆에 분모를 붙인다.
     * 편차 막대는 값만 크게 찍어서, 표와 달리 「0%」가 몇 명 중 몇 명인지 안 보였다.
     */
    const basis = metric.key === 'conversion' ? c.basis?.conversion : undefined;

    return {
        metric,
        // 화면에 그대로 나가는 값이라 여기서 자릿수를 정리한다 (round1 주석 참고)
        value: round1(value),
        avg: round1(avg),
        score,
        weightedScore: score * weight,
        diffPct: Math.round(raw * 1000) / 10,
        kind:
            score >= MEANINGFUL_DEVIATION
                ? 'STRENGTH'
                : score <= -MEANINGFUL_DEVIATION
                  ? 'WEAKNESS'
                  : 'NEUTRAL',
        judgeable: weight > 0,
        detail:
            basis && basis.denominator > 0 ? `${basis.denominator}명 중 ${basis.numerator}명` : undefined,
    };
};

export type CoachAnalysis = {
    coach: CoachRow;
    /** 전 지표 편차 (신호 강한 순) */
    deviations: Deviation[];
    strengths: Deviation[];
    weaknesses: Deviation[];
    /** 표본이 부족해 판정하지 못한 지표 */
    unmeasured: MetricDef[];
    /** 종합 점수 — 편차 평균 */
    overall: number;
    /**
     * 종합 등급.
     *
     * ⚠️ NA 는 "판정할 지표가 하나도 없다"는 뜻입니다. GOOD 과 다릅니다.
     * 예전에는 판정 가능한 지표가 0개면 그대로 GOOD('안정')으로 떨어졌습니다.
     * 이제 막 합류해 전환·일지·노쇼가 전부 null 인 강사가 화면에서
     * "안정 · 평균 수준으로 무리 없이 운영되고 있습니다" 로 표시됐고,
     * 원장님은 그걸 보고 그 강사를 안 챙겼습니다.
     * 아무것도 재지 못했으면 아무 말도 하지 않는 것이 맞습니다.
     */
    /**
     * ⚠️ INSUFFICIENT 는 "표본이 모자라 평가를 접었다"는 뜻입니다. NA 와 다릅니다.
     *   NA           잴 지표가 하나도 없다 (센터 평균이 없거나 이 강사 값이 전부 비어 있다)
     *   INSUFFICIENT 지표는 나왔지만 분모가 너무 작아 그 숫자를 믿을 수 없다
     * 원장이 할 일이 다릅니다 — 앞은 기다리는 것이고, 뒤는 회원 배정·수업을 늘리는 것입니다.
     */
    grade: 'EXCELLENT' | 'GOOD' | 'WATCH' | 'CRITICAL' | 'NA' | 'INSUFFICIENT';
    /** 표본 판정 — 배지에 이유를 적으려면 등급만으로는 부족하다 */
    sample: SampleCheck;
    /** 가장 급한 약점 하나 (없으면 null) */
    topIssue: Deviation | null;
};

export const GRADE_META: Record<
    CoachAnalysis['grade'],
    { label: string; desc: string; color: string; bg: string; border: string }
> = {
    EXCELLENT: {
        label: '핵심 인재',
        desc: '여러 지표가 센터 평균을 크게 웃돕니다',
        color: '#00795F',
        bg: '#EAF7F2',
        border: '#B7DFD2',
    },
    GOOD: {
        label: '안정',
        desc: '평균 수준으로 무리 없이 운영되고 있습니다',
        color: '#2C4C8C',
        bg: '#F1F5FC',
        border: '#D3E0F5',
    },
    WATCH: {
        label: '점검 필요',
        desc: '평균을 밑도는 지표가 있습니다',
        color: '#B45309',
        bg: '#FEF3C7',
        border: '#F0D9A8',
    },
    CRITICAL: {
        label: '집중 관리',
        desc: '핵심 지표가 크게 떨어집니다. 원인부터 확인하세요',
        color: '#C22D38',
        bg: '#FFF0F0',
        border: '#FBD0D3',
    },
    NA: {
        label: '판단 보류',
        desc: '아직 판단할 수 있는 지표가 없습니다. 기록이 쌓이면 평가합니다',
        // 회색 — 좋다고도 나쁘다고도 읽히면 안 된다
        color: '#7C8794',
        bg: '#F1F4F9',
        border: '#E3EAF3',
    },
    INSUFFICIENT: {
        label: '평가 유보',
        desc: '표본이 적어 지표를 믿을 수 없습니다. 좋다 나쁘다를 아직 말할 수 없습니다',
        /*
         * NA 와 같은 회색 계열이다.
         * 유보를 노란색·빨간색으로 칠하면 "문제 있는 강사"로 읽히는데,
         * 유보는 문제가 있다는 뜻이 아니라 **아직 말할 수 없다**는 뜻이다.
         */
        color: '#7C8794',
        bg: '#F1F4F9',
        border: '#E3EAF3',
    },
};

/**
 * 강사 한 명 분석.
 * @param avgLessons 수업량 평균 — 센터 상수가 없어 목록에서 구해 넘긴다
 */
/**
 * 센터 평균을 못 받았을 때 쓰는 기준선 — 전부 null 이다.
 *
 * ⚠️ 실화면(강사 현황·리포트 팝업·PDF·상세 패널)에서 서버 `centerAvg` 가 없을 때
 * 쓰는 값입니다. **여기에도, 이 값을 쓰는 화면에도 목 상수(COACH_CENTER_AVG)를
 * 넣지 마세요.**
 *
 * 그 상수는 목 데이터에서 나온 값(전환율 31.7%, 일지 48% 등)이라,
 * 서버 응답이 없거나 늦은 한 프레임 동안 실제 강사가 **남의 센터 기준**으로
 * CRITICAL/WATCH(화면) · S/A/B/C(PDF) 판정을 받습니다. 이제 막 문을 연 센터에서는
 * 멀쩡한 강사가 약점투성이로, 그 반대로도 뒤집힙니다.
 * 그 판정은 면담 자리와 인쇄물에 그대로 올라갑니다.
 *
 * 전부 null 이면 비교 지표가 판정 불가로 떨어지고(deviationOf → null,
 * readMetric → verdict 'UNKNOWN'), 등급은 'NA'('아직 판단할 수 없습니다')로
 * 수렴합니다. 기준을 모르면 아무 말도 하지 않는 것이 맞습니다.
 */
export const NO_CENTER_AVG: CenterAvg = {
    conversionPct: null,
    lessonsPerActiveMember: null,
    /* 세 세대의 밀도 평균이 전부 '기준 없음'이어야 한다 — 하나라도 빠지면 그쪽으로 샌다 */
    lessonsPerMonthlyMember: null,
    lessonsPerLessonMember: null,
    closingRate: null,
    sameDayCancel: null,
    responseHours: null,
    journalPct: null,
    journalReadPct: null,
    noshowPct: null,
    injuryPct: null,
};

/**
 * 지표별 센터 평균 꺼내기.
 *
 * ⚠️ 이 함수가 없으면 METRICS 에 박힌 상수(COACH_CENTER_AVG)와 비교하게 됩니다.
 * 그 상수는 목 데이터에서 나온 값이라, 실서버에서는
 *   · 이제 막 문을 연 센터가 "회원당 수업 0회 vs 평균 11.2회 → 심각한 약점"
 *   · 운영 방식이 다른 센터가 남의 기준으로 평가
 * 되는 일이 벌어집니다. 비교 기준은 반드시 **그 센터의 실제 평균**이어야 합니다.
 */
export const centerAvgOf = (
    avg: CenterAvg | undefined,
    key: MetricKey,
    /*
     * 🔴 밀도 평균은 **그 강사의 분모와 짝이 맞아야** 고를 수 있다.
     *    안 넘기면 옛 기준(오늘 담당) 평균으로 떨어진다 — 월 스코프 강사에게
     *    그 평균을 대면 분모가 다른 값끼리 비교하게 되므로, 밀도를 쓰는
     *    호출부는 반드시 강사를 함께 넘길 것.
     */
    coach?: Pick<CoachRow, 'activeMembers' | 'monthlyMembers'>
): number | null | undefined => {
    if (!avg) return undefined; // 안 넘겼으면 METRICS 기본값을 쓴다 (목 모드)
    switch (key) {
        case 'conversion':
            return avg.conversionPct;
        case 'closing':
            return avg.closingRate;
        case 'response':
            return avg.responseHours;
        case 'sameDayCancel':
            return avg.sameDayCancel;
        case 'journal':
            return avg.journalPct;
        case 'journalRead':
            return avg.journalReadPct;
        case 'noshow':
            return avg.noshowPct;
        case 'injury':
            return avg.injuryPct;
        case 'lessonDensity':
            return lessonDensityAvgOf(avg, coach);
        default:
            return undefined;
    }
};

export const analyzeCoach = (
    c: CoachRow,
    avgLessons: number,
    centerAvg?: CenterAvg
): CoachAnalysis => {
    const deviations: Deviation[] = [];
    const unmeasured: MetricDef[] = [];

    METRICS.forEach((m) => {
        // 수업량은 이 센터 강사들의 실제 평균으로만 비교한다
        // null 을 그대로 넘긴다 — '기준 없음'과 '안 넘김'은 다른 뜻이다.
        // ?? undefined 로 뭉개면 기준 없는 지표가 목 상수와 비교된다.
        const override = m.key === 'lessons' ? avgLessons : centerAvgOf(centerAvg, m.key, c);
        const d = deviationOf(c, m, override);
        if (d) deviations.push(d);
        else unmeasured.push(m);
    });

    // 신호가 강한 순 — 잘하든 못하든 튀는 것부터 보여준다
    deviations.sort((a, b) => Math.abs(b.score) - Math.abs(a.score));

    const strengths = deviations.filter((d) => d.kind === 'STRENGTH').sort((a, b) => b.score - a.score);
    const weaknesses = deviations.filter((d) => d.kind === 'WEAKNESS').sort((a, b) => a.score - b.score);

    /*
     * 등급과 1순위 문제는 판정 가능한 지표로만 정한다.
     * 당일취소는 회원 요청분이 섞여 있고, 수업량은 담당 회원 수에 따라 달라진다.
     * 이런 지표로 "이 강사가 문제다"라고 하면 면담에서 근거를 댈 수 없다.
     * 화면에는 그대로 보여주되 판정에서만 뺀다.
     */
    const judgeable = deviations.filter((d) => d.judgeable);
    const judgeableWeak = judgeable.filter((d) => d.kind === 'WEAKNESS').sort((a, b) => a.weightedScore - b.weightedScore);
    const judgeableStrong = judgeable.filter((d) => d.kind === 'STRENGTH');

    const overall = judgeable.length
        ? judgeable.reduce((s, d) => s + d.score, 0) / judgeable.length
        : 0;

    // 전환율은 센터 매출을 좌우해서 따로 본다. 여기가 무너지면 나머지가 좋아도 집중 관리다.
    const conversion = deviations.find((d) => d.metric.key === 'conversion');
    const conversionBroken = conversion != null && conversion.score <= -0.3;

    const sample = checkSample(c);

    let grade: CoachAnalysis['grade'];
    /*
     * 판정 가능한 지표가 하나도 없으면 등급을 매기지 않는다.
     * 예전에는 여기서 모든 분기를 빠져나가 마지막 else 의 GOOD('안정')이 됐다.
     * 지표가 0개라는 것은 "문제가 없다"가 아니라 "아직 재지 못했다"이고,
     * 둘을 같은 배지로 보여주면 신규 강사가 조용히 방치된다.
     * reportComposer.gradeOf 도 같은 이유로 canCompare 가 false 면 'NA' 를 준다.
     */
    if (judgeable.length === 0) grade = 'NA';
    /*
     * 표본이 모자라면 지표가 나왔더라도 평가하지 않는다.
     *
     * 담당 1명·수업 3회짜리 강사가 '핵심 인재'로 찍히던 경로가 여기였다.
     * 전환율은 그 한 명이 재등록했으니 100%, 노쇼율은 세 번 다 왔으니 0% —
     * 둘 다 만점이라 강점 두 개가 잡히고 EXCELLENT 로 떨어졌다.
     * 그 숫자는 강사의 실력이 아니라 표본 크기의 부작용이다.
     *
     * ⚠️ NA 보다 **뒤에** 둔다. 잴 지표가 아예 없는 것이 더 앞선 사실이다.
     * ⚠️ CRITICAL 보다 **앞에** 둔다. 표본 1명으로 매긴 '집중 관리'도 똑같이 틀렸다.
     *    낮은 등급도 평가다.
     */
    else if (!sample.enough) grade = 'INSUFFICIENT';
    else if (conversionBroken || judgeableWeak.length >= 2) grade = 'CRITICAL';
    else if (judgeableWeak.length >= 1) grade = 'WATCH';
    else if (overall >= 0.2 && judgeableStrong.length >= 1 && strengths.length >= 2) grade = 'EXCELLENT';
    else grade = 'GOOD';

    return {
        coach: c,
        deviations,
        strengths,
        weaknesses,
        unmeasured,
        overall: Math.round(overall * 1000) / 10,
        grade,
        sample,
        /*
         * 유보 강사에게는 '1순위 문제'도 주지 않는다.
         * 표본 1명에서 나온 약점을 "가장 급한 문제"라고 내놓으면
         * 원장이 그 근거로 면담을 연다.
         */
        topIssue: sample.enough ? (judgeableWeak[0] ?? null) : null,
    };
};

/**
 * @param centerAvg 서버가 준 이 센터의 평균. 안 넘기면 목 상수를 쓴다.
 *                  실화면에서는 **반드시 넘겨야** 남의 기준으로 평가하지 않는다.
 */
export const analyzeAll = (rows: CoachRow[], centerAvg?: CenterAvg): CoachAnalysis[] => {
    /*
     * 수업량 평균은 여기서 나눠서 만든다 — 센터 상수가 없는 유일한 지표라서다.
     * 나눈 값을 그대로 두면 화면에 "평균 43.55555555555556회"로 박힌다.
     * (강사 현황 화면도 compareMetric 에 넘길 때 같은 자리에서 반올림한다)
     */
    return rows.map((c) => analyzeCoach(c, eligibleLessonsAvg(rows), centerAvg));
};

/**
 * 오늘 챙겨야 할 강사.
 *
 * 집중 관리 → 점검 필요 순으로, 같은 등급이면 담당 회원 미사용 선수금이 큰 사람 먼저.
 * 같은 문제라도 8백만 원을 들고 있는 강사가 급하다.
 */
export const pickCoachesToAct = (list: CoachAnalysis[], limit = 3): CoachAnalysis[] => {
    const rank: Record<CoachAnalysis['grade'], number> = {
        CRITICAL: 0,
        WATCH: 1,
        GOOD: 2,
        EXCELLENT: 3,
        // 판정 자체를 못 한 강사는 '오늘 챙길 강사' 명단에 올리지 않는다.
        // 아래 filter 에서 이미 빠지지만, 정렬 표에도 자리는 있어야 한다.
        NA: 4,
        /*
         * 표본 부족도 마찬가지다.
         * 아래 filter 가 CRITICAL·WATCH 만 남기므로 유보 강사는 자동으로 빠진다 —
         * 오늘의 브리핑 강사 편성(briefingComposition.pickCoachInsight)도
         * 이 함수를 쓰므로 거기서도 같이 빠진다.
         */
        INSUFFICIENT: 5,
    };
    return [...list]
        .filter((a) => a.grade === 'CRITICAL' || a.grade === 'WATCH')
        .sort((a, b) => {
            if (rank[a.grade] !== rank[b.grade]) return rank[a.grade] - rank[b.grade];
            return b.coach.portfolioValue - a.coach.portfolioValue;
        })
        .slice(0, limit);
};

/** 배우고 공유할 만한 강사 — 칭찬은 근거가 있어야 힘이 있다 */
export const pickCoachToLearnFrom = (list: CoachAnalysis[]): CoachAnalysis | null => {
    const best = [...list]
        .filter((a) => a.grade === 'EXCELLENT' && a.strengths.length > 0)
        .sort((a, b) => b.overall - a.overall);
    return best[0] ?? null;
};

/* ───────────────────────── 지표 비교 ───────────────────────── */

export type PeerPoint = {
    label: string;
    value: number;
    /** 지금 보고 있는 강사인가 */
    isMine: boolean;
    /** 이 값이 좋은 쪽인가 */
    good: boolean;
};

export type MetricComparison = {
    metric: MetricDef;
    mine: number;
    avg: number;
    /**
     * 이 강사가 센터에서 몇 위인가 (1이 가장 좋음).
     * 모집단에서 자기 자리를 못 찾으면 `null` 이다 — 0 위로 찍지 않는다.
     */
    rank: number | null;
    /** 순위를 매길 수 있었던 강사 수. 순위가 없으면 null */
    rankOf: number | null;
    /** 막대로 그릴 비교 대상 — 나 · 평균 · 1위 · 최하위 */
    points: PeerPoint[];
    /**
     * 이 비교를 어떤 톤으로 그릴 것인가.
     *
     * 🔴 화면이 `rank` 로 톤을 다시 재지 않는다. 판정을 아는 것은 계산기뿐이므로
     *    여기서 정해서 내려보낸다.
     *
     *   `STRONG`  잘하는 쪽 — 활용 문구
     *   `WEAK`    처지는 쪽 — 개선 문구
     *   `NEUTRAL` 순위를 못 정한 경우 — **우열 문구를 내지 않는다**
     */
    tone: 'STRONG' | 'WEAK' | 'NEUTRAL';
};

/**
 * 지표 하나를 센터 전체와 비교한다.
 *
 * "전환율 21.4% (평균 31.7%)"까지는 칩에 들어가지만,
 * 1위가 58.3%라는 걸 알아야 "이만큼 벌어져 있구나"가 실감난다.
 * 면담에서 근거로 쓰려면 이 폭이 보여야 한다.
 */
export const compareMetric = (
    coach: CoachRow,
    metric: MetricDef,
    all: CoachRow[],
    /** deviationOf 와 같은 규칙 — null 은 '기준 없음'이라 비교하지 않는다 */
    avgOverride?: number | null
): MetricComparison | null => {
    const mine = metric.get(coach);
    const avg = avgOverride === undefined ? metric.avg : avgOverride;
    if (mine == null || !avg) return null;

    /*
     * 순위 모집단.
     *   · 값이 없는 강사는 뺀다 — null 을 0으로 두면 꼴찌로 잡힌다.
     *   · 표본이 모자란 강사도 뺀다 — 담당 1명의 전환율 100% 가 끼면
     *     정상 강사 전원이 한 칸씩 밀린다.
     *
     * 단, **보고 있는 본인은 표본과 무관하게 넣는다.** 빼면 findIndex 가 -1 이 되어
     * 순위가 0 으로 찍힌다. 유보 강사가 이 비교를 여는 경로는 원본 표뿐이고,
     * 그때 보여줄 것은 '적격 강사들 사이에서 이 값이 어디쯤인가'다.
     */
    const measured = all
        .filter((c) => c.coachId === coach.coachId || checkSample(c).enough)
        .map((c) => ({ c, v: metric.get(c) }))
        .filter((x): x is { c: CoachRow; v: number } => x.v != null)
        .sort((a, b) => (metric.higherIsBetter ? b.v - a.v : a.v - b.v));

    /* findIndex 가 -1 이면 0 위가 되므로 그 경우는 null 로 접는다 */
    const idx = measured.findIndex((x) => x.c.coachId === coach.coachId);
    const rank = idx >= 0 ? idx + 1 : null;
    const best = measured[0];
    const worst = measured[measured.length - 1];

    // 막대 옆에 그대로 찍히는 숫자다. 자릿수는 여기서 정리한다 (round1 주석 참고)
    const points: PeerPoint[] = [
        {
            label: `${coach.name} 강사`,
            value: round1(mine),
            isMine: true,
            /* 순위를 못 정했으면 '좋은 쪽'도 단정하지 않는다 — 중립으로 그린다 */
            good: rank != null && rank <= Math.ceil(measured.length / 2),
        },
        { label: '센터 평균', value: round1(avg), isMine: false, good: false },
    ];
    if (best && best.c.coachId !== coach.coachId) {
        points.push({ label: `1위 ${best.c.name} 강사`, value: round1(best.v), isMine: false, good: true });
    }
    if (worst && worst.c.coachId !== coach.coachId && worst.c.coachId !== best?.c.coachId) {
        points.push({ label: `최하위 ${worst.c.name} 강사`, value: round1(worst.v), isMine: false, good: false });
    }

    /* 톤은 센터 절반을 기준으로 가른다. 순위를 못 정했으면 우열도 말하지 않는다. */
    const tone: MetricComparison['tone'] =
        rank == null ? 'NEUTRAL' : rank > Math.ceil(measured.length / 2) ? 'WEAK' : 'STRONG';

    return {
        metric,
        mine: round1(mine),
        avg: round1(avg),
        rank,
        rankOf: rank == null ? null : measured.length,
        points,
        tone,
    };
};
